import { FC, useMemo, useCallback, ReactNode } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Table from '@/components/Tables/Table/Table';
import Icon from '@/components/Icons/Icon';
import { Modifier } from '@/types/menus.types';
import {
  namedPriceDeactivateFutureLabel,
  namedPriceShowsGlobalDefaultPrice,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/namedPriceModifier.helpers';
import { useSTKContext } from '@/context/context';
import styles from '../../DeactivateProductsDrawer/DeactivateProductsDrawerTabItems/ProductsTable.module.css';
import drawerStyles from '../../DeactivateProductsDrawer/DeactivateProductsDrawer.module.css';

type FutureState = { mode: 'label'; label: string } | { mode: 'activeCircle' };

interface ModifiersTableProps {
  storeModifiers: Modifier[];
  futureState: FutureState;
  /** Hug the row content so a named-price summary can sit in the same card. */
  fitContent?: boolean;
}

interface ModifierRow {
  modifierName: string;
  modifierDescription: string;
  showsDefaultPriceAlert?: boolean;
  [key: string]: unknown;
}

const renderFutureStateCell = (futureState: FutureState, value: unknown): ReactNode => {
  if (futureState.mode === 'activeCircle') {
    return (
      <div className={styles.priceWithIcon}>
        <span
          className={drawerStyles.futureStateActiveCircle}
          data-testid="future-state-active-circle"
          aria-label="Active"
        />
      </div>
    );
  }

  return (
    <div className={styles.priceWithIcon}>
      <span className={styles.inactive}>{typeof value === 'string' ? value : futureState.label}</span>
    </div>
  );
};

const ModifiersTable: FC<ModifiersTableProps> = ({ storeModifiers, futureState, fitContent = false }) => {
  const { t } = useTranslation();
  const { storesAndGroups } = useSTKContext();

  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    if (storesAndGroups.origin === 'tab-store') {
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet);
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      return storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || [];
    }

    return [];
  }, [storesAndGroups]);

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  const tableData = useMemo(() => {
    if (!storeModifiers || storeModifiers.length === 0) {
      return [];
    }

    return storeModifiers.map((modifier) => {
      const cellValue =
        futureState.mode === 'label'
          ? modifier.namedPriceId
            ? namedPriceDeactivateFutureLabel(modifier.defaultPrice, futureState.label)
            : futureState.label
          : 'active';
      const row: ModifierRow = {
        modifierName: modifier.modifierName || '',
        modifierDescription: modifier.modifierDescription || '',
        showsDefaultPriceAlert:
          Boolean(modifier.namedPriceId) && namedPriceShowsGlobalDefaultPrice(modifier.defaultPrice),
      };

      allStoreGroupNames.forEach((storeGroupName) => {
        row[storeGroupName] = cellValue;
      });

      return row;
    });
  }, [storeModifiers, allStoreGroupNames, futureState]);

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        id: 'modifier',
        accessorKey: 'modifierName',
        header: () => <div className={styles.productHeader}>{t('editMenuPrice.modifierColumn')}</div>,
        cell: (info: any) => {
          const row = info.row.original as ModifierRow;
          const showDescription = !fitContent || Boolean(row.modifierDescription);

          return (
            <div className={styles.productCell}>
              {row.showsDefaultPriceAlert && (
                <span data-testid="default-price-alert-icon" className={styles.productAlertIcon}>
                  <Icon
                    iconName="AlertCircle"
                    size={18}
                    style={{ color: 'var(--red-700)', stroke: 'var(--red-700)' }}
                  />
                </span>
              )}
              <div className={styles.productText}>
                <div className={styles.productName}>{info.getValue()}</div>
                {showDescription ? <div className={styles.productDescription}>{row.modifierDescription}</div> : null}
              </div>
            </div>
          );
        },
        size: 350,
        minSize: 250,
        maxSize: 450,
      },
    ];

    allStoreGroupNames.forEach((storeGroupName) => {
      const { count, total } = getStoreGroupsCount(storeGroupName);

      cols.push({
        id: storeGroupName,
        accessorKey: storeGroupName,
        header: () => (
          <div className={styles.headerWithCount}>
            <div className={styles.headerName} title={storeGroupName}>
              {storeGroupName.toUpperCase()}
            </div>
            <div className={styles.headerCount}>
              {count} / {total}
            </div>
          </div>
        ),
        cell: (info: any) => renderFutureStateCell(futureState, info.getValue()),
        size: 145,
        minSize: 130,
        maxSize: 160,
      });
    });

    return cols;
  }, [allStoreGroupNames, getStoreGroupsCount, t, futureState, fitContent]);

  const hasManyStoreGroups = allStoreGroupNames.length >= 5;

  return (
    <div className={styles.container}>
      <div className={classNames(styles.tableWrapper, { [styles.manyStoreGroups]: hasManyStoreGroups })}>
        <Table
          data={tableData}
          columns={columns}
          includeMinWidth={false}
          height={fitContent ? 'auto' : 'calc(68px + (76px * 3))'}
          className={styles.tableScroll}
        />
      </div>
    </div>
  );
};

export default ModifiersTable;
